import {Placement} from '@floating-ui/react-dom';
import React, {FC, ReactNode, RefObject, useRef} from 'react';
import styled from 'styled-components';

import {InteractionComponentProps} from '../../../../core/src/helpers/interaction/interactionHelpers';
import {Renderer, RendererOf} from '../../../../core/src/helpers/react/reactHelpers';
import {PopoverCoordinator, PopoverTransferProps} from '../popovers/popoverCoordinator';
import {TooltipCoordinator} from '../tooltips/tooltipCoordinator';
import {DropdownBox, DropdownBoxProps} from './dropdownBox';

/*
 * Props.
 */

export interface DropdownBoxCoordinatorProps extends DropdownBoxProps, InteractionComponentProps {
  /** Render the dropdown to show when clicked. */
  renderDropdown: RendererOf<PopoverTransferProps>;
  /** Render the dropdown button. */
  renderButton?: (isOpen: boolean, props: DropdownBoxCoordinatorProps) => ReactNode;
  /** Render a tooltip under the button by default. */
  renderTooltip?: Renderer;
  /** Placement of the tooltip the button. */
  tooltipPlacement?: Placement;
  /** Should PreventDefault on MouseDown events. */
  shouldPreventMouseDown?: boolean;
  className?: string;
  onPopoverToggle?: (arg: boolean) => void;
  onRemove?: () => void;
  backgroundColor?: string;
  isModalInitiallyOpen?: boolean;
  shouldDisablePadding?: boolean;
  tabIndex?: number;
  /** Makes the dropdown width match its content instead of filling the entire container. */
  shouldFitContent?: boolean;
  customBoxRef?: RefObject<HTMLDivElement>;
}

/*
 * Style.
 */

interface WrapperStyleProps {
  $hasLabel?: boolean;
  $shouldDisablePadding?: boolean;
}
const StyledWrapperDiv = styled.div<WrapperStyleProps>`
  padding: ${(p) => {
    if (p.$shouldDisablePadding) {
      return '0';
    }
    return p.$hasLabel ? '0 0 12px 0' : '12px 0';
  }};
`;

/*
 * Component.
 */

export const DropdownBoxCoordinator: FC<DropdownBoxCoordinatorProps> = (props) => {
  const boxRef = useRef<HTMLDivElement>(null);
  return (
    <StyledWrapperDiv
      $hasLabel={Boolean(props.label)}
      $shouldDisablePadding={props.shouldDisablePadding}
      className={props.className}
    >
      <PopoverCoordinator
        targetRef={props.customBoxRef ?? boxRef}
        renderButton={(isOpen) => renderButton(isOpen, props, props.customBoxRef ?? boxRef)}
        renderModal={props.renderDropdown}
        shouldPreventMouseDown={props.shouldPreventMouseDown}
        isDisabled={props.isDisabled}
        interactionId={props.interactionId}
        onPopoverToggle={props.onPopoverToggle}
        isModalInitiallyOpen={props.isModalInitiallyOpen}
        tabIndex={props.tabIndex}
      />
    </StyledWrapperDiv>
  );
};

/*
 * DropdownBox.
 */

function renderButton(
  isOpen: boolean,
  props: DropdownBoxCoordinatorProps,
  boxRef: RefObject<HTMLDivElement>,
) {
  if (props.renderButton) {
    return props.renderButton(isOpen, props);
  }

  return (
    <TooltipCoordinator render={props.renderTooltip} placement={props.tooltipPlacement}>
      <DropdownBox
        {...props}
        hasHover={isOpen}
        noInnerMargin
        ref={boxRef}
        shouldFitContent={props.shouldFitContent}
        hasFocus={isOpen}
      />
    </TooltipCoordinator>
  );
}
